<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NpsSurveysAPI from 'dashboard/api/npsSurveys';
import NextButton from 'dashboard/components-next/button/Button.vue';

const SCORES = Array.from({ length: 11 }, (_, index) => index);

// Each browser session (tab) shows a survey at most once; the server counts how many sessions saw it.
const shownKey = id => `nps_shown_${id}`;
const wasShownInSession = id => {
  try {
    return sessionStorage.getItem(shownKey(id)) !== null;
  } catch {
    return false;
  }
};
const markShownInSession = id => {
  try {
    sessionStorage.setItem(shownKey(id), '1');
  } catch {
    // storage unavailable: the server-side limit still applies
  }
};

const { t } = useI18n();

const survey = ref(null);
const scores = ref({});
const comments = ref({});
const isSubmitting = ref(false);

const questions = computed(() => survey.value?.questions || []);
const requiredQuestionId = computed(() => questions.value[0]?.id);
const canSubmit = computed(
  () => scores.value[requiredQuestionId.value] !== undefined
);

const scoreColor = score => `hsl(${score * 12}, 70%, 45%)`;

const fetchActiveSurvey = async () => {
  try {
    const { data } = await NpsSurveysAPI.active();
    const active = data.nps_survey || null;
    if (!active || wasShownInSession(active.id)) {
      survey.value = null;
      return;
    }
    survey.value = active;
    markShownInSession(active.id);
    NpsSurveysAPI.impression(active.id).catch(() => {});
  } catch {
    survey.value = null;
  }
};

const selectScore = (questionId, score) => {
  const isOptional = questionId !== requiredQuestionId.value;
  if (isOptional && scores.value[questionId] === score) {
    const { [questionId]: _removed, ...rest } = scores.value;
    scores.value = rest;
    const { [questionId]: _comment, ...restComments } = comments.value;
    comments.value = restComments;
    return;
  }
  scores.value = { ...scores.value, [questionId]: score };
};

const close = () => {
  survey.value = null;
};

const submit = async () => {
  if (!canSubmit.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await NpsSurveysAPI.respond(survey.value.id, {
      answers: Object.entries(scores.value).map(([questionId, score]) => ({
        question_id: Number(questionId),
        score,
        comment: comments.value[questionId]?.trim() || undefined,
      })),
    });
    survey.value = null;
    useAlert(t('GENERAL_SETTINGS.NPS_MODAL.THANKS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.NPS_MODAL.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(fetchActiveSurvey);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="survey"
    class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-n-alpha-black1 backdrop-blur-[4px]"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="relative flex flex-col w-full max-w-2xl gap-6 p-6 overflow-y-auto shadow-xl max-h-[90vh] rounded-xl bg-n-solid-2"
    >
      <button
        type="button"
        class="absolute p-1 border-0 rounded cursor-pointer top-3 right-3 bg-transparent text-n-slate-10 hover:text-n-slate-12"
        :aria-label="$t('GENERAL_SETTINGS.NPS_MODAL.DISMISS')"
        data-testid="nps-dismiss"
        @click="close"
      >
        <span class="i-lucide-x size-4" />
      </button>

      <div
        v-for="question in questions"
        :key="question.id"
        class="flex flex-col gap-3"
        :data-testid="`nps-question-${question.id}`"
      >
        <h3 class="m-0 text-base font-medium text-center text-n-slate-12">
          {{ question.text }}
        </h3>
        <div class="flex justify-between gap-1">
          <button
            v-for="score in SCORES"
            :key="score"
            type="button"
            class="flex-1 h-10 text-sm font-medium text-white border-2 border-transparent rounded cursor-pointer"
            :class="{
              'opacity-60': scores[question.id] === undefined,
              'opacity-25':
                scores[question.id] !== undefined &&
                scores[question.id] !== score,
              '!border-n-slate-12': scores[question.id] === score,
            }"
            :style="{ backgroundColor: scoreColor(score) }"
            :data-testid="`nps-score-${question.id}-${score}`"
            :aria-pressed="scores[question.id] === score"
            @click="selectScore(question.id, score)"
          >
            {{ score }}
          </button>
        </div>
        <div class="flex justify-between text-xs text-n-slate-10">
          <span>{{ $t('GENERAL_SETTINGS.NPS_MODAL.UNLIKELY') }}</span>
          <span>{{ $t('GENERAL_SETTINGS.NPS_MODAL.LIKELY') }}</span>
        </div>
        <textarea
          v-if="scores[question.id] !== undefined"
          v-model="comments[question.id]"
          rows="3"
          maxlength="1000"
          class="w-full !mb-0"
          :placeholder="$t('GENERAL_SETTINGS.NPS_MODAL.COMMENT_PLACEHOLDER')"
          :data-testid="`nps-comment-${question.id}`"
        />
      </div>

      <div v-if="canSubmit" class="flex justify-end">
        <NextButton
          :label="$t('GENERAL_SETTINGS.NPS_MODAL.SUBMIT')"
          :is-loading="isSubmitting"
          data-testid="nps-submit"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
